import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ContentContainer,
  ModalTitle,
  FormContainer,
  InputLabel,
  StyledInput,
  StyledTextarea,
  InputWrapper,
  FieldsRow,
  FieldHalf,
  ButtonsContainer,
  CancelButton,
  SubmitButton,
} from "./EditCustomProcedureModal.styled";
import { Select } from "../../Select";
import { SelectOption } from "../../Select/Select";
import { cdtCodesService, CDTCode } from "../../../services/cdtCodesService";
import { CustomProcedure } from "../../../services/organizationService";

export interface EditCustomProcedureFormData {
  name: string;
  cdtCodeIds: number[];
  fee: number;
  durationMinutes: number;
  description: string;
}

interface EditCustomProcedureModalContentProps {
  procedure: CustomProcedure;
  onSave: (data: EditCustomProcedureFormData) => void | Promise<void>;
  onClose: () => void;
}

const EditCustomProcedureModalContent: React.FC<
  EditCustomProcedureModalContentProps
> = ({ procedure, onSave, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<EditCustomProcedureFormData>({
    name: procedure.name,
    cdtCodeIds: [],
    fee: procedure.fee,
    durationMinutes: procedure.durationMinutes,
    description: procedure.description || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cdtCodes, setCdtCodes] = useState<CDTCode[]>([]);
  const [cdtLoading, setCdtLoading] = useState(true);

  // Fetch CDT codes on mount and map procedure codes to IDs
  useEffect(() => {
    const fetchCdtCodes = async () => {
      try {
        setCdtLoading(true);
        const codes = await cdtCodesService.getCDTCodes({ activeOnly: true });
        setCdtCodes(codes);

        // Map procedure.codes (strings) to IDs
        const codeIds = procedure.codes
          .map((codeStr) => {
            const cdtCode = codes.find((c) => c.code === codeStr);
            return cdtCode?.id || 0;
          })
          .filter((id) => id > 0);

        setFormData((prev) => ({
          ...prev,
          cdtCodeIds: codeIds,
        }));
      } catch (error) {
        console.error("Error fetching CDT codes:", error);
      } finally {
        setCdtLoading(false);
      }
    };
    fetchCdtCodes();
  }, [procedure.codes]);

  // CDT options for select
  const cdtOptions: SelectOption[] = useMemo(
    () =>
      cdtCodes.map((code) => ({
        value: code.code,
        label: `${code.code} - ${code.description}`,
        chipLabel: code.code,
      })),
    [cdtCodes],
  );

  // Get selected CDT code strings from IDs
  const selectedCdtCodeStrings = useMemo(() => {
    // First try to match from fetched cdtCodes
    const fromCdtCodes = formData.cdtCodeIds
      .map((id) => {
        const code = cdtCodes.find((c) => c.id === id);
        return code?.code || "";
      })
      .filter(Boolean);

    // If cdtCodes not loaded yet, use procedure.codes
    if (fromCdtCodes.length === 0 && procedure.codes.length > 0) {
      return procedure.codes;
    }

    return fromCdtCodes;
  }, [formData.cdtCodeIds, cdtCodes, procedure.codes]);

  const handleInputChange = (
    field: keyof EditCustomProcedureFormData,
    value: string | number | number[],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle CDT code selection change
  const handleCdtCodeChange = (
    value: string | number | (string | number)[],
  ) => {
    const codeStrings = Array.isArray(value)
      ? (value as string[])
      : [value as string];

    const codeIds = codeStrings
      .map((codeStr) => {
        const cdtCode = cdtCodes.find((c) => c.code === codeStr);
        return cdtCode?.id || 0;
      })
      .filter((id) => id > 0);

    handleInputChange("cdtCodeIds", codeIds);

    // Auto-calculate fee and duration from selected codes
    const selectedCodesData = codeIds
      .map((id) => cdtCodes.find((c) => c.id === id))
      .filter(Boolean) as CDTCode[];

    if (selectedCodesData.length > 0) {
      const totalFee = selectedCodesData.reduce(
        (sum, code) => sum + (code.defaultFee || 0),
        0,
      );
      const totalDuration = selectedCodesData.reduce(
        (sum, code) => sum + (code.defaultDurationMinutes || 0),
        0,
      );

      setFormData((prev) => ({
        ...prev,
        cdtCodeIds: codeIds,
        fee: totalFee,
        durationMinutes: totalDuration,
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || formData.cdtCodeIds.length === 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error("Error updating custom procedure:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name.trim() && formData.cdtCodeIds.length > 0;

  return (
    <ContentContainer>
      <ModalTitle>
        {t("editCustomProcedureModal.title", "Edit Custom Procedure")}
      </ModalTitle>

      <FormContainer>
        <InputWrapper>
          <InputLabel>
            {t("editCustomProcedureModal.procedureName", "Procedure Name")}
          </InputLabel>
          <StyledInput
            type="text"
            placeholder={t(
              "editCustomProcedureModal.procedureNamePlaceholder",
              "Enter procedure name",
            )}
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            disabled={isSubmitting}
          />
        </InputWrapper>

        <InputWrapper>
          <Select
            label={t("editCustomProcedureModal.cdtCode", "CDT Code")}
            placeholder={t(
              "editCustomProcedureModal.cdtCodePlaceholder",
              "Select CDT codes",
            )}
            value={selectedCdtCodeStrings}
            onChange={handleCdtCodeChange}
            options={cdtOptions}
            multiple
            searchable
            showChips
            loading={cdtLoading}
            fullWidth
          />
        </InputWrapper>

        <FieldsRow>
          <FieldHalf>
            <InputLabel>
              {t("editCustomProcedureModal.duration", "Duration, min")}
            </InputLabel>
            <StyledInput
              type="number"
              min="0"
              placeholder="0"
              value={formData.durationMinutes || ""}
              onChange={(e) =>
                handleInputChange(
                  "durationMinutes",
                  parseInt(e.target.value) || 0,
                )
              }
              disabled={isSubmitting}
            />
          </FieldHalf>
          <FieldHalf>
            <InputLabel>
              {t("editCustomProcedureModal.fee", "Fee, $")}
            </InputLabel>
            <StyledInput
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
              value={formData.fee || ""}
              onChange={(e) =>
                handleInputChange("fee", parseFloat(e.target.value) || 0)
              }
              disabled={isSubmitting}
            />
          </FieldHalf>
        </FieldsRow>

        <InputWrapper>
          <InputLabel>
            {t("editCustomProcedureModal.description", "Description")}
          </InputLabel>
          <StyledTextarea
            placeholder={t(
              "editCustomProcedureModal.descriptionPlaceholder",
              "Add description...",
            )}
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            disabled={isSubmitting}
          />
        </InputWrapper>
      </FormContainer>

      <ButtonsContainer>
        <CancelButton
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
        >
          {t("editCustomProcedureModal.cancel", "Cancel")}
        </CancelButton>
        <SubmitButton
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !isFormValid}
        >
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("editCustomProcedureModal.saveChanges", "Save Changes")
          )}
        </SubmitButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default EditCustomProcedureModalContent;
